'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import type { CMSFaq, CMSFaqCategories } from '@/lib/cms/types';
import { scrollToFaqSection } from '../faqAnchors';
import { FaqWrap } from '../FaqWrap';

import styles from './FaqCategories.module.scss';

export interface FaqCategoriesProps extends Pick<CMSFaqCategories, 'categoryLabel' | 'faqWraps'> {
  /** Label for "All questions" link */
  allQuestionsLabel?: string;
  /** Optional className */
  className?: string;
}

/**
 * Chevron icon for accordion
 */
function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/**
 * FaqCategories component
 *
 * Displays multiple FAQ sections with a sidebar navigation.
 * On mobile, shows an accordion to access category links.
 * On desktop, shows a sticky sidebar with all category links.
 */
export function FaqCategories({
  categoryLabel,
  faqWraps,
  allQuestionsLabel,
  className,
}: Readonly<FaqCategoriesProps>) {
  const t = useTranslations('faq');
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const wraps = faqWraps?.filter((wrap): wrap is CMSFaq => Boolean(wrap?.heading)) ?? [];

  if (wraps.length === 0) {
    return null;
  }

  const navItems = wraps.map((wrap) => ({
    sectionSysId: wrap.sys.id,
    label: wrap.heading as string,
  }));

  const resolvedAllQuestionsLabel = allQuestionsLabel ?? t('allTheQuestions');

  const handleLinkClick = (sectionSysId: string) => {
    scrollToFaqSection(sectionSysId);
    setIsAccordionOpen(false);
  };

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.inner}>
        {/* Mobile: Accordion navigation */}
        <div className={styles.linksWrapperMobile}>
          {categoryLabel ? <h2 className={styles.categoriesTitle}>{categoryLabel}</h2> : null}

          <div className={styles.accordion}>
            <button
              type="button"
              className={styles.accordionButton}
              onClick={() => setIsAccordionOpen(!isAccordionOpen)}
              aria-expanded={isAccordionOpen}
            >
              <span>{resolvedAllQuestionsLabel}</span>
              <ChevronIcon
                className={clsx(styles.accordionIcon, isAccordionOpen && styles.accordionIconOpen)}
              />
            </button>

            <div
              className={clsx(
                styles.accordionContent,
                isAccordionOpen && styles.accordionContentOpen
              )}
            >
              {navItems.map((item) => (
                <div key={item.sectionSysId} className={styles.linkButtonWrapper}>
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => handleLinkClick(item.sectionSysId)}
                  >
                    {item.label}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Grid layout */}
        <div className={styles.gridLayout}>
          {/* Desktop: Sidebar navigation */}
          <div className={clsx(styles.linksWrapperDesktop, styles.linksWrapperDesktopSticky)}>
            {categoryLabel ? (
              <h2 className={styles.categoriesTitleDesktop}>{categoryLabel}</h2>
            ) : null}

            <div className={styles.linkButtonWrapper}>
              <button
                type="button"
                className={clsx(styles.linkButton, styles.linkButtonUnderline)}
                onClick={() => handleLinkClick(navItems[0].sectionSysId)}
              >
                {resolvedAllQuestionsLabel}
              </button>
            </div>

            {navItems.map((item) => (
              <div key={item.sectionSysId} className={styles.linkButtonWrapper}>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => handleLinkClick(item.sectionSysId)}
                  data-testid="handleScroll"
                >
                  {item.label}
                </button>
              </div>
            ))}
          </div>

          {/* FAQ content */}
          <div className={styles.faqListWrapper}>
            {wraps.map((wrap) => (
              <FaqWrap
                key={wrap.sys.id}
                sectionSysId={wrap.sys.id}
                heading={wrap.heading}
                categories={wrap.categories}
                collapse={wrap.collapse ?? undefined}
                isFromFaqWrapCategories={true}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

FaqCategories.displayName = 'FaqCategories';
